/**
 * @file Per-functional-unit compound units (kg CO₂e/kg, MJ/kg) from Background finished material unit.
 */

import {
  buildA1A3CarbonUnitOptions,
  perFinishedCarbonUnitLabel,
  perFinishedCarbonUnitValue,
  syncA1A3UnitSelection,
} from "../A1-A3/a1a3SummaryFromSubmission";
import { normalizeUnitToken } from "./fuelMixReferenceAutofill";
import { resolveUnitSelectValueFromLabel } from "./unitSelectOptions";

/** CMS / API field names for embodied carbon per functional unit — not reference qty unit. */
export function isEmbodiedCarbonFunctionalUnitFieldName(
  fieldName: string
): boolean {
  const n = String(fieldName ?? "").toLowerCase();
  if (!n.includes("unit")) return false;
  if (n.includes("referenceqty")) return false;
  if (n.includes("reference") && !n.includes("functional")) return false;

  return (
    n.includes("functionalunit") ||
    n.includes("functionalc") ||
    n.includes("embodiedcarbondetailsfunctionalunit") ||
    n === "unitembodiedcarbonfunctionalunit"
  );
}

/** Build `<select>` options + value for embodied carbon per functional unit. */
export function buildEmbodiedCarbonFunctionalUnitSelectState(
  denominatorSymbol: string | null,
  selected: string
): { options: { value: string; label: string }[]; selectValue: string } {
  const options = buildA1A3CarbonUnitOptions(denominatorSymbol);
  const selectValue = syncA1A3UnitSelection(
    selected,
    selected,
    options,
    Boolean(denominatorSymbol?.trim())
  );
  return { options, selectValue };
}

/** Human-readable label for read-only views. */
export function resolveEmbodiedCarbonFunctionalUnitDisplayLabel(
  denominatorSymbol: string | null,
  selected: string
): string {
  const trimmed = String(selected ?? "").trim();
  if (!trimmed) return "";

  const { options, selectValue } = buildEmbodiedCarbonFunctionalUnitSelectState(
    denominatorSymbol,
    trimmed
  );
  const match = options.find((o) => o.value === selectValue);
  if (match?.label) return match.label;

  if (trimmed.includes("/")) {
    return perFinishedCarbonUnitLabel(denominatorSymbol);
  }

  return trimmed;
}

/** Valid stored values for functional-unit carbon unit fields (compound + legacy ids). */
export function embodiedCarbonFunctionalUnitValidValues(
  denominatorSymbol: string | null,
  cmsUnitOptionValues: string[] = []
): Set<string> {
  const values = new Set<string>(cmsUnitOptionValues);
  for (const o of buildA1A3CarbonUnitOptions(denominatorSymbol)) {
    values.add(o.value);
  }
  return values;
}

/** Resolve form value to numeric unit id for save APIs. */
export function resolveEmbodiedCarbonFunctionalUnitForSave(
  formValue: string,
  unitIdByToken: Record<string, number>,
  denominatorSymbol: string | null,
  kgCo2eUnitId = ""
): number | null {
  const v = String(formValue ?? "").trim();
  if (!v) return null;
  if (/^\d+$/.test(v)) return Number(v);

  const perVal = perFinishedCarbonUnitValue(denominatorSymbol);
  const candidates = [v, perVal].filter(Boolean);
  for (const candidate of candidates) {
    const id = resolveUnitSelectValueFromLabel(candidate, unitIdByToken);
    if (id && /^\d+$/.test(id)) return Number(id);
  }

  const den = denominatorSymbol?.trim();
  if (den) {
    const denId = resolveUnitSelectValueFromLabel(den, unitIdByToken);
    if (denId && /^\d+$/.test(denId)) return Number(denId);
  }

  if (kgCo2eUnitId && /^\d+$/.test(kgCo2eUnitId)) {
    return Number(kgCo2eUnitId);
  }

  return null;
}

/** Whether a bare unit (MJ, kg CO₂e) should upgrade to per-denominator when Background qty unit is known. */
export function shouldUpgradeBareUnitToPerDenominator(
  current: string,
  options: { value: string }[]
): boolean {
  const c = String(current ?? "").trim();
  if (!c || c.includes("/")) return false;
  const bare = options.find((o) => !String(o.value).includes("/"));
  return bare != null && bare.value === c;
}

/** Normalize legacy numeric unit id to compound token when denominator is known. */
export function normalizeEmbodiedCarbonFunctionalUnitFormValue(
  stored: string,
  denominatorSymbol: string | null,
  kgCo2eUnitId = ""
): string {
  const trimmed = String(stored ?? "").trim();
  if (!denominatorSymbol?.trim()) return trimmed;
  if (trimmed.includes("/")) return trimmed;

  const perVal = perFinishedCarbonUnitValue(denominatorSymbol);
  if (!trimmed) return perVal;

  if (kgCo2eUnitId && trimmed === kgCo2eUnitId) {
    return perVal;
  }

  const normStored = normalizeUnitToken(trimmed);
  const normBare = normalizeUnitToken(
    perFinishedCarbonUnitValue(null)
  );
  if (normStored === normBare) return perVal;

  return trimmed;
}
